import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { Tooltip } from './Tooltip'
import type { Placement } from './floating'

export type IconButtonTone = 'paper' | 'chrome'
export type IconButtonVariant = 'ghost' | 'solid' | 'danger'
export type IconButtonSize = 'sm' | 'md' | 'lg'

const SIZES: Record<IconButtonSize, string> = {
  sm: 'h-[30px] w-[30px]',
  md: 'h-9 w-9',
  lg: 'h-11 w-11',
}

const TONES: Record<IconButtonTone, Record<IconButtonVariant, string>> = {
  paper: {
    ghost: 'text-ink-mute hover:bg-sunken hover:text-ink',
    solid: 'bg-surface text-ink border border-hair hover:bg-sunken',
    danger: 'text-down hover:bg-sunken',
  },
  chrome: {
    ghost: 'text-chrome-mute hover:bg-chrome-700 hover:text-chrome-ink',
    solid: 'bg-chrome-800 text-chrome-ink border border-chrome-600 hover:bg-chrome-700',
    danger: 'text-down hover:bg-chrome-700',
  },
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /** Required: this control has no visible text, so the label is its only name. */
  label: string
  icon: ReactNode
  variant?: IconButtonVariant
  size?: IconButtonSize
  tone?: IconButtonTone
  /** Shows `label` on hover and focus. Off when the control sits in a menu row. */
  tooltip?: boolean
  tooltipPlacement?: Placement
  active?: boolean
  className?: string
}

/** A square control carrying one icon. The label is mandatory and doubles as the tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    icon,
    variant = 'ghost',
    size = 'md',
    tone = 'paper',
    tooltip = true,
    tooltipPlacement = 'bottom',
    active = false,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={active || undefined}
      className={cn(
        'ring-focus inline-flex items-center justify-center rounded-sm transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-45',
        SIZES[size],
        TONES[tone][variant],
        active && (tone === 'chrome' ? 'bg-chrome-700 text-chrome-ink' : 'bg-sunken text-ink'),
        className,
      )}
      {...rest}
    >
      <span aria-hidden="true">{icon}</span>
    </button>
  )

  if (!tooltip) return button
  return (
    <Tooltip content={label} placement={tooltipPlacement}>
      {button}
    </Tooltip>
  )
})
